// HTTP-клиент: JWT в Authorization, авто-refresh при 401, типизированные ответы.
import { diag, newRequestId, normalizePath, shouldLogApi } from '../lib/diagLog';
import { decideRefresh, parseTokenPair, sameTokenSession, serializeTokenPair, tokenSessionIdentity, type RefreshResult, type TokenPair } from './refreshRace';
import i18n from '../i18n';

const ACCESS_KEY = 'hc_access';
const REFRESH_KEY = 'hc_refresh';
const TOKEN_PAIR_KEY = 'hc_token_pair';
const CLEARED_TOKEN_STATE = '!cleared!';

export class ApiError extends Error {
  status: number;
  readonly reportId: string;
  readonly category: 'http' | 'network' | 'parse' | 'unknown';
  constructor(status: number, message: string, reportId = newRequestId(), category: ApiError['category'] = 'http') {
    const key = category !== 'http' ? category : status >= 500 ? 'server' : status === 429 ? 'rateLimit' : status === 403 ? 'forbidden' : status === 404 ? 'notFound' : 'request';
    const detail = category === 'http' && status < 500 && message ? message : i18n.t('misc:requestErrors.' + key);
    super(detail + ' ' + i18n.t('misc:requestErrors.reportCode', { code: reportId }));
    this.status = status;
    this.reportId = reportId;
    this.category = category;
  }
}

let localSessionEpoch = 0;

type TokenPairStorage = TokenPair;

function getTokenPair(): TokenPairStorage | null {
  const stored = localStorage.getItem(TOKEN_PAIR_KEY);
  if (stored === CLEARED_TOKEN_STATE) return null;
  if (stored) {
    const parsed = parseTokenPair(stored);
    if (parsed) return parsed;
    // Повреждённый новый формат: попробуем legacy-ключи ниже.
  }
  const accessToken = localStorage.getItem(ACCESS_KEY);
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  return accessToken && refreshToken ? { accessToken, refreshToken } : null;
}

export function getAccessToken(): string | null {
  if (localStorage.getItem(TOKEN_PAIR_KEY) === CLEARED_TOKEN_STATE) return null;
  return getTokenPair()?.accessToken ?? localStorage.getItem(ACCESS_KEY);
}

export function setTokens(access: string, refresh: string) {
  const current = getTokenPair();
  const newSession = !current || !sameTokenSession(current.accessToken, access);
  // Одна localStorage-запись не оставляет половину новой пары при закрытии вкладки.
  localStorage.setItem(TOKEN_PAIR_KEY, serializeTokenPair({ accessToken: access, refreshToken: refresh }));
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  if (newSession) localSessionEpoch++;
}

/** Идентификатор локального входа: refresh его сохраняет, повторный вход меняет. */
export function getAuthSession(): string | null {
  try {
    const identity = tokenSessionIdentity(getAccessToken());
    return identity ? localSessionEpoch + ':' + identity : null;
  } catch { return null; }
}

export function clearTokens() {
  // Tombstone first so a crash during legacy-key cleanup cannot resurrect the old pair.
  localStorage.setItem(TOKEN_PAIR_KEY, CLEARED_TOKEN_STATE);
  ++localSessionEpoch;
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getRefreshToken(): string | null {
  if (localStorage.getItem(TOKEN_PAIR_KEY) === CLEARED_TOKEN_STATE) return null;
  return getTokenPair()?.refreshToken ?? localStorage.getItem(REFRESH_KEY);
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

// Single-flight внутри вкладки; Web Locks объединяет refresh между вкладками одного origin.
type RefreshFlight = { epoch: number; identity: string | null; promise: Promise<boolean> };
let refreshInFlight: RefreshFlight | null = null;

export function tryRefresh(
  staleAccess?: string | null,
  staleRefresh?: string | null,
): Promise<boolean> {
  const stored = getTokenPair();
  const accessToken = staleAccess === undefined ? stored?.accessToken ?? null : staleAccess;
  const refreshToken = staleRefresh === undefined ? stored?.refreshToken ?? null : staleRefresh;
  if (!accessToken || !refreshToken) return Promise.resolve(false);
  const sent: TokenPair = { accessToken, refreshToken };
  const epoch = localSessionEpoch;
  const identity = tokenSessionIdentity(sent.accessToken);
  if (refreshInFlight?.epoch === epoch && refreshInFlight.identity === identity) return refreshInFlight.promise;

  // Сразу после lock повторно читаем pair: вкладка, которая ждала победителя, использует его R2.
  const run: Promise<boolean> = typeof navigator !== 'undefined' && navigator.locks
    ? (navigator.locks.request('hc-refresh', async () => doRefresh(sent, epoch)) as unknown as Promise<boolean>)
    : doRefresh(sent, epoch);
  const wrapped = run.finally(() => {
    if (refreshInFlight?.promise === wrapped) refreshInFlight = null;
  });
  refreshInFlight = { epoch, identity, promise: wrapped };
  return wrapped;
}

async function doRefresh(sent: TokenPair, startEpoch: number): Promise<boolean> {
  const current = getTokenPair();
  if (!current || !sameTokenSession(sent.accessToken, current.accessToken)) return false;
  if (current.refreshToken !== sent.refreshToken) return true;
  if (startEpoch !== localSessionEpoch) return false;

  let result: RefreshResult;
  try {
    const rid = newRequestId();
    const res = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Request-Id': rid },
      body: JSON.stringify({ refreshToken: current.refreshToken }),
    });
    diag({ kind: 'refresh', what: 'refresh', status: res.status, rid });
    if (!res.ok) {
      result = { kind: 'http-error', status: res.status };
    } else {
      const json = await res.json() as { accessToken: string; refreshToken: string };
      result = { kind: 'success', tokens: { accessToken: json.accessToken, refreshToken: json.refreshToken } };
    }
  } catch (error) {
    diag({ kind: 'refresh', what: 'refresh', status: 0, note: error instanceof Error ? error.name : 'error' });
    result = { kind: 'network-error' };
  }

  const decision = decideRefresh({
    sent,
    current: getTokenPair(),
    startEpoch,
    currentEpoch: localSessionEpoch,
    result,
  });
  switch (decision.action) {
    case 'apply':
      setTokens(decision.tokens.accessToken, decision.tokens.refreshToken);
      return true;
    case 'use-current':
      return true;
    case 'clear':
      clearTokens();
      return false;
    case 'keep':
    case 'ignore':
      return false;
  }
}

/** Достаём читаемое сообщение из RFC 7807 тела ответа (detail/title/fieldErrors). */
async function errorDetail(res: Response): Promise<string> {
  let detail = '';
  try {
    const body = await res.json();
    detail = body.detail || body.title || detail;
    if (body.fieldErrors?.length) {
      detail = body.fieldErrors.map((f: { field: string; message: string }) => `${f.field}: ${f.message}`).join('; ');
    }
  } catch {
    /* нет тела */
  }
  return detail;
}

const responseRequestIds = new WeakMap<Response, string>();

/** fetch с X-Request-Id; неудачные/медленные запросы пишутся в журнал диагностики (без тел и токенов). */
async function tracedFetch(path: string, init: RequestInit): Promise<Response> {
  const rid = newRequestId();
  // Accept-Language: backend переводит тексты ошибок на язык интерфейса (см. ErrorMessageTranslator).
  const headers = { 'Accept-Language': i18n.resolvedLanguage ?? 'ru', ...(init.headers as Record<string, string> | undefined), 'X-Request-Id': rid };
  const started = performance.now();
  const what = `${init.method ?? 'GET'} ${normalizePath(path)}`;
  try {
    const res = await fetch(path, { ...init, headers });
    responseRequestIds.set(res, res.headers.get('X-Request-Id') ?? rid);
    const ms = Math.round(performance.now() - started);
    if (shouldLogApi(res.status, ms)) {
      diag({ kind: 'api', what, status: res.status, ms, rid: res.headers.get('X-Request-Id') ?? rid });
    }
    return res;
  } catch (e) {
    diag({ kind: 'api', what, status: 0, ms: Math.round(performance.now() - started), rid, note: e instanceof Error ? e.name : 'error' });
    if (e instanceof Error && e.name === 'AbortError') throw e;
    throw new ApiError(0, '', rid, e instanceof TypeError ? 'network' : 'unknown');
  }
}

async function requestApi<T>(path: string, opts: RequestInit, retry: boolean, authenticated: boolean, expectedSession?: string): Promise<T> {
  if (expectedSession !== undefined && getAuthSession() !== expectedSession) throw new DOMException('Сессия изменилась', 'AbortError');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> | undefined),
  };
  const stored = authenticated ? getTokenPair() : null;
  const access = authenticated ? stored?.accessToken ?? getAccessToken() : null;
  const refresh = authenticated ? stored?.refreshToken ?? getRefreshToken() : null;
  if (access) headers.Authorization = 'Bearer ' + access;
  else if (!authenticated) {
    delete headers.Authorization;
    delete headers.authorization;
  }

  const res = await tracedFetch(path, { ...opts, headers });
  if (expectedSession !== undefined && getAuthSession() !== expectedSession) throw new DOMException('Сессия изменилась', 'AbortError');

  if (authenticated && res.status === 401 && retry && (await tryRefresh(access, refresh))) {
    return requestApi<T>(path, opts, false, true, expectedSession);
  }
  if (!res.ok) {
    throw new ApiError(res.status, await errorDetail(res), responseRequestIds.get(res));
  }
  if (res.status === 204) return undefined as T;
  return parseResponse<T>(res);
}

/** Отправка очереди не может перейти на токены другого входа во время refresh. */
export function apiForSession<T>(session: string, path: string, opts: RequestInit): Promise<T> {
  return requestApi<T>(path, opts, true, true, session);
}

export function api<T>(path: string, opts: RequestInit = {}, retry = true): Promise<T> {
  return requestApi<T>(path, opts, retry, true);
}

/** Публичный запрос без Bearer и без refresh-реакции на 401 (вход, регистрация и QR до подтверждения). */
export function apiUnauthenticated<T>(path: string, opts: RequestInit = {}): Promise<T> {
  return requestApi<T>(path, opts, false, false);
}

export async function apiUpload<T>(path: string, formData: FormData, retry = true): Promise<T> {
  const headers: Record<string, string> = {};
  const stored = getTokenPair();
  const access = stored?.accessToken ?? getAccessToken();
  const refresh = stored?.refreshToken ?? getRefreshToken();
  if (access) headers.Authorization = 'Bearer ' + access;
  const res = await tracedFetch(path, { method: 'POST', headers, body: formData });
  if (res.status === 401 && retry && (await tryRefresh(access, refresh))) {
    return apiUpload<T>(path, formData, false);
  }
  if (!res.ok) throw new ApiError(res.status, await errorDetail(res), responseRequestIds.get(res));
  return parseResponse<T>(res);
}

/** UUID v4, работает без secure context (в отличие от crypto.randomUUID()). */
export function randomUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

async function parseResponse<T>(res: Response): Promise<T> {
  try { return await res.json() as T; }
  catch {
    const rid = responseRequestIds.get(res) ?? newRequestId();
    diag({ kind: 'api', what: 'response:parse', status: res.status, rid, note: 'SyntaxError' });
    throw new ApiError(res.status, '', rid, 'parse');
  }
}
